import { useEffect, useMemo, useRef, useState } from 'react';
import { UMButton, UMIcon } from '../../urbanmap/_components';
import { countModifiedRatios, isValidRatio, ratioTotal } from './domain';
import { PC, UMR, caption } from './tokens';
import {
  SHEET_COLS, SHEET_ROWS, applyMatrix, cellKey, cellValue, clamp, inRect, parseClipboard,
  parseNum, rectOf, rectSize, resetRect, serializeRect, setCell, type Cell, type Rect,
} from './sheet';
import type { RatioTable as Ratios } from './types';
import { Pill } from '../proposition-c/ui';

interface Props {
  value: Ratios;
  defaults: Ratios;
  onChange: (next: Ratios) => void;
  onValidityChange?: (valid: boolean) => void;
}

const th: React.CSSProperties = {
  padding: '6px 8px', fontSize: 12, lineHeight: '16px', fontWeight: 700, color: UMR.textLabel,
  background: UMR.grey100, borderBottom: `1px solid ${UMR.border}`, borderRight: `1px solid ${UMR.borderSubtle}`,
  position: 'sticky', top: 0, zIndex: 1, textAlign: 'center',
};
const input: React.CSSProperties = {
  width: '100%', height: 28, border: 'none', outline: 'none', background: 'transparent',
  font: `400 13px/20px ${UMR.font}`, textAlign: 'right', padding: '0 6px',
  fontVariantNumeric: 'tabular-nums', color: UMR.black,
};

/** Ratios en mode feuille de calcul : sélection au clic-glissé, copier/coller multi-cellules. */
export function RatioSheet({ value, defaults, onChange, onValidityChange }: Props) {
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [anchor, setAnchor] = useState<Cell>({ r: 0, c: 0 });
  const [sel, setSel] = useState<Rect>({ r1: 0, c1: 0, r2: 0, c2: 0 });
  const [note, setNote] = useState('');
  const dragging = useRef(false);
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});

  const invalid = useMemo(() => Object.entries(texts).filter(([, t]) => !isValidRatio(parseNum(t))).map(([k]) => k), [texts]);
  const modified = countModifiedRatios(value, defaults);
  useEffect(() => { onValidityChange?.(invalid.length === 0); }, [invalid.length, onValidityChange]);
  useEffect(() => {
    const up = () => { dragging.current = false; };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  const select = (cell: Cell, extend = false) => {
    if (extend) setSel(rectOf(anchor, cell));
    else { setAnchor(cell); setSel({ r1: cell.r, c1: cell.c, r2: cell.r, c2: cell.c }); }
  };
  const focusCell = (cell: Cell) => {
    const el = inputs.current[cellKey(cell.r, cell.c)];
    if (el) { el.focus(); el.select(); }
  };
  const commit = (r: number, c: number, raw: string) => {
    setTexts((t) => ({ ...t, [cellKey(r, c)]: raw }));
    const n = parseNum(raw);
    if (isValidRatio(n)) onChange(setCell(value, r, c, n));
  };

  const onCopy = (e: React.ClipboardEvent) => {
    if (rectSize(sel) <= 1) return;
    e.clipboardData.setData('text/plain', serializeRect(value, sel));
    e.preventDefault();
    setNote(`${rectSize(sel)} cellules copiées`);
  };
  const onPaste = (e: React.ClipboardEvent) => {
    const matrix = parseClipboard(e.clipboardData.getData('text/plain'));
    if (matrix.length === 0) return;
    e.preventDefault();
    const { next, applied, skipped } = applyMatrix(value, anchor, matrix);
    setTexts({});
    onChange(next);
    setSel(rectOf(anchor, {
      r: clamp(anchor.r + matrix.length - 1, SHEET_ROWS.length - 1),
      c: clamp(anchor.c + Math.max(...matrix.map((l) => l.length)) - 1, SHEET_COLS.length - 1),
    }));
    setNote(`${applied} valeur${applied > 1 ? 's' : ''} collée${applied > 1 ? 's' : ''}${skipped ? ` · ${skipped} ignorée${skipped > 1 ? 's' : ''} (hors 1-1 000)` : ''}`);
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    const move = (dr: number, dc: number) => {
      const cell = { r: clamp(anchor.r + dr, SHEET_ROWS.length - 1), c: clamp(anchor.c + dc, SHEET_COLS.length - 1) };
      e.preventDefault();
      if (e.shiftKey) setSel(rectOf(anchor, cell));
      else { select(cell); focusCell(cell); }
    };
    if (e.key === 'ArrowDown' || e.key === 'Enter') move(1, 0);
    else if (e.key === 'ArrowUp') move(-1, 0);
    else if (e.shiftKey && e.key === 'ArrowRight') move(0, 1);
    else if (e.shiftKey && e.key === 'ArrowLeft') move(0, -1);
    else if ((e.key === 'Delete' || e.key === 'Backspace') && rectSize(sel) > 1) {
      e.preventDefault();
      setTexts({});
      onChange(resetRect(value, defaults, sel));
      setNote(`${rectSize(sel)} cellules remises par défaut`);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontFamily: UMR.font }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: UMR.textSecondary }}>
          <UMIcon name="content_paste" size={16} color={UMR.textSecondary} />
          Sélectionnez une plage puis <strong style={{ color: UMR.black }}>Ctrl+C</strong> / <strong style={{ color: UMR.black }}>Ctrl+V</strong> depuis Excel — lignes et colonnes d'un coup.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {modified > 0 && <Pill tone="accent" icon="edit">{modified} valeur{modified > 1 ? 's' : ''} modifiée{modified > 1 ? 's' : ''}</Pill>}
          <UMButton
            variant="subtle"
            icon="restart_alt"
            disabled={modified === 0 && invalid.length === 0}
            onClick={() => { setTexts({}); onChange(resetRect(value, defaults, { r1: 0, c1: 0, r2: SHEET_ROWS.length - 1, c2: SHEET_COLS.length - 1 })); setNote(''); }}
          >
            Tout réinitialiser
          </UMButton>
        </div>
      </div>

      <div
        role="grid"
        aria-label="Feuille des ratios de consommation"
        tabIndex={-1}
        onCopy={onCopy}
        onPaste={onPaste}
        onKeyDown={onKeyDown}
        style={{ maxHeight: 420, overflow: 'auto', border: `1px solid ${UMR.border}`, background: UMR.white }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', userSelect: 'none' }}>
          <colgroup>
            <col style={{ width: 150 }} />
            <col style={{ width: 90 }} />
            {SHEET_COLS.map((p) => <col key={p.key} style={{ width: 110 }} />)}
          </colgroup>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left', left: 0, zIndex: 2 }}>Typologie</th>
              <th style={{ ...th, textAlign: 'left' }}>Poste</th>
              {SHEET_COLS.map((p, c) => (
                <th key={p.key} style={{ ...th, color: c >= sel.c1 && c <= sel.c2 ? PC.accentStrong : UMR.textLabel }}>{p.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SHEET_ROWS.map((row, r) => {
              const lastOfUsage = (r + 1) % 3 === 0;
              const inSelRow = r >= sel.r1 && r <= sel.r2;
              return (
                <tr key={`${row.u}.${row.e}`}>
                  {row.first && (
                    <td rowSpan={3} style={{ padding: '6px 8px', fontSize: 13, fontWeight: 700, verticalAlign: 'top', background: UMR.grey100, borderRight: `1px solid ${UMR.border}`, borderBottom: `1px solid ${UMR.border}` }}>
                      {row.usageLabel}
                    </td>
                  )}
                  <td style={{ padding: '4px 8px', fontSize: 12, color: inSelRow ? PC.accentStrong : UMR.textSecondary, background: UMR.grey100, borderRight: `1px solid ${UMR.border}`, borderBottom: `1px solid ${lastOfUsage ? UMR.border : UMR.borderSubtle}` }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <UMIcon name={row.icon} size={14} color={inSelRow ? PC.accentStrong : UMR.textSecondary} />
                      {row.energyLabel}
                    </span>
                  </td>
                  {SHEET_COLS.map((p, c) => {
                    const k = cellKey(r, c);
                    const text = texts[k] ?? String(cellValue(value, r, c));
                    const bad = invalid.includes(k);
                    const changed = !bad && cellValue(value, r, c) !== cellValue(defaults, r, c);
                    const selected = inRect(sel, r, c);
                    const isAnchor = anchor.r === r && anchor.c === c;
                    return (
                      <td
                        key={p.key}
                        role="gridcell"
                        aria-selected={selected}
                        onMouseDown={(e) => { dragging.current = true; select({ r, c }, e.shiftKey); }}
                        onMouseEnter={() => { if (dragging.current) setSel(rectOf(anchor, { r, c })); }}
                        style={{
                          padding: 0,
                          background: bad ? PC.dangerBg : selected ? PC.accentBg : changed ? UMR.grey100 : UMR.white,
                          outline: isAnchor ? `2px solid ${PC.accent}` : undefined,
                          outlineOffset: -2,
                          borderRight: `1px solid ${UMR.borderSubtle}`,
                          borderBottom: `1px solid ${lastOfUsage ? UMR.border : UMR.borderSubtle}`,
                          borderLeft: changed && !selected ? `3px solid ${PC.accent}` : `3px solid transparent`,
                        }}
                        title={bad ? 'Valeur invalide : nombre entre 1 et 1 000' : `Défaut : ${cellValue(defaults, r, c)} kWh/m²/an`}
                      >
                        <input
                          ref={(el) => { inputs.current[k] = el; }}
                          value={text}
                          inputMode="decimal"
                          aria-label={`${row.usageLabel} — ${row.energyLabel} — ${p.label}`}
                          onFocus={() => select({ r, c })}
                          onChange={(e) => commit(r, c, e.target.value)}
                          style={{ ...input, color: bad ? PC.dangerText : UMR.black, userSelect: 'text' }}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2} style={{ padding: '6px 8px', fontSize: 12, fontWeight: 700, color: UMR.textLabel, background: UMR.grey100, borderRight: `1px solid ${UMR.border}` }}>
                Total résidentiel collectif
              </td>
              {SHEET_COLS.map((p) => (
                <td key={p.key} style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, fontSize: 13, fontVariantNumeric: 'tabular-nums', background: UMR.grey100 }}>
                  {ratioTotal(value.resCollectif[p.key])}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, ...caption }}>
        <UMIcon name="info" size={14} color={UMR.textSecondary} />
        kWh/m²/an · {rectSize(sel)} cellule{rectSize(sel) > 1 ? 's' : ''} sélectionnée{rectSize(sel) > 1 ? 's' : ''} · Suppr. remet la sélection par défaut.
        {note && <span style={{ color: PC.accentStrong, fontWeight: 700 }}>{note}</span>}
        {invalid.length > 0 && <span style={{ color: PC.dangerText, fontWeight: 700 }}>{invalid.length} valeur{invalid.length > 1 ? 's' : ''} invalide{invalid.length > 1 ? 's' : ''} (1 à 1 000)</span>}
      </div>
    </div>
  );
}
